import { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
// Msal imports
import { MsalAuthenticationTemplate, useMsal } from "@azure/msal-react";
import { InteractionStatus, InteractionType, InteractionRequiredAuthError } from "@azure/msal-browser";
import { loginRequest, callMsGraph } from "shared/libs/azure-msal";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { isBlankOrNull } from "utils";
import { useLogin, useGetRoleUser, useGetRoleUserSca } from "shared/services/auth/auth.service";
import {
  setAuthenticated,
  setUser,
  setRole,
  setPermissions,
  clearInfo,
  setGraphInfo,
  setActivateAccount,
} from "shared/stores/reducers/authSlice";
import * as notistack from "notistack";

export function Profile({ children }) {
  const authRequest = { ...loginRequest };
  const dispatch = useDispatch();
  const { instance, inProgress } = useMsal();
  const [graphData, setGraphData] = useState(null);
  const getUserInfo = useLogin();
  const getPermissionScreen = useGetRoleUser();
  const getPermissionScreenSca = useGetRoleUserSca();
  const navigate = useNavigate();

  useEffect(() => {
    if (!graphData && inProgress === InteractionStatus.None) {
      callMsGraph(instance)
        .then(async response => {
          setGraphData(response);
          dispatch(setAuthenticated(true));
          dispatch(setGraphInfo(response));
          // const { idTokenClaims } = instance.getActiveAccount();
          // console.log("roles >> ", idTokenClaims.roles);
          let userInfo = null;
          if (process.env.REACT_APP_ENV_NAME === "master") {
            userInfo = await handleUserInfoSca(response, instance, dispatch, getUserInfo, getPermissionScreenSca);
          } else {
            // For support non-prod coz TASC dose have non-prod env
            userInfo = await handleUserInfo(response, instance, dispatch, getUserInfo, getPermissionScreen);
          }

          if (typeof userInfo === "boolean" && !userInfo) {
            navigate("error");
            return;
          }
        })
        .catch(e => {
          console.error(e);
          if (e instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect({
              ...loginRequest,
              scopes: [process.env.REACT_APP_SCOPES],
              account: instance.getActiveAccount(),
            });
          } else {
            dispatch(clearInfo());
            navigate("error");
          }
        });
    }

    console.info("graphData", graphData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress, graphData, instance]);

  return (
    <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={authRequest}>
      <Paper>{children}</Paper>
    </MsalAuthenticationTemplate>
  );
}
// For support non-prod coz TASC dose have non-prod env
export async function handleUserInfo(response, instance, dispatch, getUserInfo, getPermissionScreen) {
  try {
    if (isBlankOrNull(instance.getActiveAccount())) {
      throw Error("No active account! Verify a user has been signed in and setActiveAccount has been called.");
    }
    dispatch(setActivateAccount(instance.getActiveAccount()));
    const { idTokenClaims } = instance.getActiveAccount();
    const payload = {
      userId: response.id ? response.id.trim() : "",
      accountName: response.givenName ? convertName(response.givenName) : "",
      country: response.country,
      companyName: response.companyName,
      email: response.mail,
      roles: idTokenClaims.roles ?? ["DEFAULT-ACCESS"],
    };

    const { result: userInfo } = await getUserInfo(payload);
    if (userInfo?.roles.length > 0) {
      const bodyUserInfo = {
        ...response,
        displayName: userInfo?.name,
        userName: userInfo?.name,
        country: userInfo.country,
        companyAbbr: userInfo.companyName,
        branch: userInfo.branch,
      };

      dispatch(setUser(bodyUserInfo));
      dispatch(setRole(userInfo?.roles));
      localStorage.setItem("appItem", userInfo?.role);

      const permissionResponse = await getPermissionScreen({
        userId: response.id,
        userName: userInfo?.name,
        roles: userInfo?.roles,
      });

      const functions = permissionResponse?.result?.functions ?? [];
      dispatch(setPermissions(functions));
    } else {
      return false;
    }
    return userInfo;
  } catch (error) {
    console.error(error);
    return false;
  }
}

export async function handleUserInfoSca(response, instance, dispatch, getUserInfo, getPermissionScreen) {
  try {
    if (isBlankOrNull(instance.getActiveAccount())) {
      throw Error("No active account! Verify a user has been signed in and setActiveAccount has been called.");
    }
    const { idTokenClaims } = instance.getActiveAccount();
    const payload = {
      userId: response.id ? response.id.trim() : "",
      accountName: response.givenName ? convertName(response.givenName) : "",
      country: response.country,
      companyName: response.companyName,
      email: response.mail,
      roles: idTokenClaims.roles ?? ["DEFAULT-ACCESS"],
    };

    const { result: userInfo } = await getUserInfo(payload);
    if (userInfo?.roles.length > 0) {
      const bodyUserInfo = {
        ...response,
        displayName: userInfo?.name,
        userName: userInfo?.name,
        country: userInfo.country,
        companyAbbr: userInfo.companyName,
        branch: userInfo.branch,
      };

      dispatch(setUser(bodyUserInfo));
      dispatch(setRole(userInfo?.roles));
      localStorage.setItem("appItem", userInfo?.role);

      const permissionResponse = await getPermissionScreen({
        userId: response.id,
        userName: userInfo?.name,
        roles: idTokenClaims.roles,
      });

      const functions = permissionResponse?.result?.functions ?? [];
      dispatch(setPermissions(functions));
    } else {
      return false;
    }
    return userInfo;
  } catch (error) {
    console.error(error);
    notistack.enqueueSnackbar(error.message, {
      variant: "error",
      anchorOrigin: {
        vertical: "top",
        horizontal: "right",
      },
    });
    return false;
  }
}

const convertName = name => {
  if (isBlankOrNull(name)) return "";
  const nameArr = name?.replace("-", " ")?.split(" ");
  return nameArr[0] || "";
};
